import { Controller, useForm } from 'react-hook-form';
import {
  Checkbox,
  Input,
  Select,
  SnackbarStatus,
  Spinner,
  StatusBar,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useRecordIdentifierSchema, useRecordIdentifiers } from 'src/hooks/useRecordIdentifiers';
import { IIdentifierDeclaration, IIdentifierWrite, updateRecordIdentifiers } from 'src/api/recordIdentifiers';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { PricingSavePortal } from 'src/components/PricingSavePortal/PricingSavePortal';
import {
  INTEGRATIONS_EMPTY,
  INTEGRATIONS_FORM_ID,
  INTEGRATIONS_LOAD_FAILURE,
  INTEGRATIONS_SAVE_SUCCESS,
  REQUIRED_BADGE,
} from './constants';
import { buildGroups, fieldName, storedValues, writesFrom } from './helpers';
import { IIntegrationEntry, IIntegrationsTabProps } from './types';
import styles from './IntegrationsTab.module.scss';

/**
 * A control with the connector's guidance beneath it.
 *
 * Only the library's Input takes helperText of its own, so the checkbox and the select have
 * the same guidance rendered alongside them. Without this a connector's description and the
 * badge saying it needs the key would show for text fields and silently vanish for the rest.
 */
const WithHelperText = ({ helperText, children }: { helperText?: string; children: ReactNode }) => (
  <div className={styles.field}>
    {children}
    {helperText && (
      <Typography className={styles.helper} size="medium">
        {helperText}
      </Typography>
    )}
  </div>
);

/**
 * The Integrations tab.
 *
 * Renders itself entirely from what the installed addons declare. A connector that wants a
 * new fact about this record declares a key on its addon, and the field appears here with
 * no change to this component.
 *
 * Every value is held as text whatever its declared type, which is what lets one table
 * hold them all. The declared type only decides which input is rendered and how core reads
 * the text back.
 */
export const IntegrationsTab = ({ recordType, recordId }: IIntegrationsTabProps) => {
  const { data: schema, isLoading: schemaLoading, isError: schemaError } = useRecordIdentifierSchema(recordType);
  const { data: values, isLoading: valuesLoading, isError: valuesError } = useRecordIdentifiers(recordType, recordId);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [saveError, setSaveError] = useState<string>();

  const groups = buildGroups(schema);
  // The addon name is only worth repeating when the record has keys from more than one.
  const showAddonName = new Set(groups.map(({ addonName }) => addonName)).size > 1;
  const defaultValues = storedValues(schema, values);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<Record<string, string>>({ values: defaultValues });

  if (schemaLoading || valuesLoading) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  if (schemaError || valuesError) {
    return <StatusBar message={INTEGRATIONS_LOAD_FAILURE} variant="error" />;
  }

  if (groups.length === 0) {
    return <p className={styles.empty}>{INTEGRATIONS_EMPTY}</p>;
  }

  const onSubmit = async (formValues: Record<string, string>) => {
    setSaveError(undefined);
    const writes: IIdentifierWrite[] = writesFrom(groups, formValues, dirtyFields);

    if (writes.length === 0) {
      return;
    }

    try {
      await updateRecordIdentifiers(recordType, recordId as string, writes);
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.recordIdentifiers, getBrandId(), recordType, recordId],
      });
      reset(formValues);
      showSnackbar({ message: INTEGRATIONS_SAVE_SUCCESS, status: SnackbarStatus.SUCCESS });
    } catch (error) {
      // Core's message names the key at fault, so it is worth more than anything generic.
      setSaveError((error as Error).message);
    }
  };

  /**
   * One instance's control within a key's group.
   *
   * Which instance a value belongs to is shown above the control by the caller, as plain
   * text that is there whether the field is empty, focused or filled. The control's own
   * label says what is being typed, because an Oracle sub level reads the same for every
   * instance and the value alone never says which one it is.
   */
  const renderEntry = (declaration: IIdentifierDeclaration, { name }: IIntegrationEntry) => {
    const { type, options = [], label } = declaration;

    if (type === 'boolean') {
      return (
        <WithHelperText key={name}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Checkbox
                label={label}
                name={name}
                checked={field.value === 'true'}
                onChange={(event) => field.onChange(event.target.checked ? 'true' : 'false')}
              />
            )}
          />
        </WithHelperText>
      );
    }

    if (type === 'select') {
      return (
        <WithHelperText key={name}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Select
                label={label}
                name={name}
                options={options.map((option) => ({ value: option, name: option }))}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </WithHelperText>
      );
    }

    return (
      <Input
        key={name}
        type={type === 'number' ? 'number' : 'text'}
        label={label}
        rows={type === 'json' ? 3 : undefined}
        register={register(name)}
      />
    );
  };

  return (
    <div className={styles.container}>
      {saveError && <StatusBar message={saveError} variant="error" />}
      <form id={INTEGRATIONS_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        {groups.map(({ id, declaration, addonName, entries }) => {
          const { label, description, required } = declaration;
          const help = [description, required ? REQUIRED_BADGE : undefined].filter(Boolean).join(' ');

          return (
            <section key={id} className={styles.group}>
              <h3 className={styles.heading}>{label}</h3>
              {showAddonName && addonName && <p className={styles.addon}>{addonName}</p>}
              {help && <p className={styles.help}>{help}</p>}
              <div className={styles.entries}>
                {entries.map((entry) => (
                  <div key={entry.name} className={styles.entry}>
                    <span className={styles.instance}>{entry.instanceName}</span>
                    {renderEntry(declaration, entry)}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </form>
      <PricingSavePortal control={control} reset={reset} form={INTEGRATIONS_FORM_ID} />
    </div>
  );
};

export { fieldName };
export default IntegrationsTab;
